import * as React from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import moment from 'lib/momentTimezone';
import { useSelector } from 'react-redux';
import sortedUniqBy from 'lodash/sortedUniqBy';
import uniqBy from 'lodash/uniqBy';
import { getProvider } from 'booking/redux/selectors';
import { GRAPHQL_CLIENT_NAMES, Moment } from 'lib/types';
import {
    DayData,
    GroupedSlots,
    Provider,
    ProvidersResult,
    Slot,
    SlotWithProvider,
    TimeControl,
    TimeSlotsControls,
    TimeSlotsQuery,
    TimeSlotsVariables,
} from 'booking/lib/types';
import { groupSlots } from 'booking/lib/utils';
import useDebounced from 'core/hooks/useDebounced';
import usePrevious from 'core/hooks/usePrevious';
import useLocationTimeZone from 'core/hooks/useLocationTimeZone';
import useBookingQuery from './useBookingQuery';

const query = gql`
    query timeSlotsWithProviders(
        $locationId: InternalId!
        $from: DateString!
        $to: DateString!
        $firstAvailable: Boolean
    ) {
        timeSlotsWithProviders(
            locationId: $locationId
            from: $from
            to: $to
            firstAvailable: $firstAvailable
        ) {
            timezone
            slotsWithProviders {
                dateTime
                provider {
                    id
                    name
                    isRelief
                    clinicTeamMemberId
                }
            }
        }
    }
`;

enum Timespan {
    Week = 'week',
    Month = 'month',
    Year = 'year',
}

type TimeSlotsProps = {
    initialDate?: string;
    initialTime?: string;
    locationId?: string;
    locationOpenDate?: string;
    useWeeklySchedule?: boolean;
};

const DATE_TO_STRING_FORMAT = 'YYYY-MM-DD';
const MAX_FUTURE_MONTHS = 15;
const MAX_FUTURE_WEEKS = 66;
const MAX_FUTURE_YEARS = 1;
const DAYS_OF_THE_WEEK = [0, 1, 2, 3, 4, 5, 6].map((day) =>
    moment().day(day).format('ddd'),
);

function maxFuture(timespan: Timespan): number {
    switch (timespan) {
        case Timespan.Week:
            return MAX_FUTURE_WEEKS;
        case Timespan.Month:
            return MAX_FUTURE_MONTHS;
        case Timespan.Year:
            return MAX_FUTURE_YEARS;
        default:
            return 0;
    }
}

function getRelativeFlags(
    todayString: string,
    date: Moment,
): Pick<DayData, 'isPast' | 'isToday'> {
    const dateString = date.format(DATE_TO_STRING_FORMAT);
    return {
        isToday: todayString === dateString,
        isPast: todayString.localeCompare(dateString) > 0,
    };
}

function createDatesVariables(
    locationId: string,
    date: Moment,
    timezone: string,
    timespan: Timespan,
): TimeSlotsVariables {
    return {
        locationId,
        from: (date.isBefore(new Date()) ? moment().tz(timezone) : date).format(
            DATE_TO_STRING_FORMAT,
        ),
        to: date.clone().add(1, timespan).format(DATE_TO_STRING_FORMAT),
        firstAvailable: false,
    };
}

function createSlotVariables(
    locationId: string,
    selectedDate: string,
    timezone: string,
): TimeSlotsVariables {
    return {
        locationId,
        from: selectedDate,
        to: moment
            .tz(selectedDate, DATE_TO_STRING_FORMAT, timezone)
            .add(1, 'day')
            .format(DATE_TO_STRING_FORMAT),
        firstAvailable: false,
    };
}

function createDay(
    date: Moment,
    hasSlots: boolean,
    selectedDate: string,
    today: string,
    openDate: Moment | null,
): DayData {
    return {
        date: date.format(DATE_TO_STRING_FORMAT),
        label: date.format('D'),
        hasSlots,
        isSelected: selectedDate === date.format(DATE_TO_STRING_FORMAT),
        isBeforeOpening: openDate ? date.isBefore(openDate, 'day') : false,
        ...getRelativeFlags(today, date),
    };
}

function filterProviderSlots(
    slots: ReadonlyArray<SlotWithProvider>,
    provider: Provider,
): string[] {
    if (provider && provider.id) {
        return slots
            .filter((slot) => slot.provider.id === provider.id)
            .map((slot) => slot.dateTime);
    }

    return sortedUniqBy(
        slots.map((slot) => slot.dateTime),
        (slot) => slot,
    );
}

function createTimespanDays(
    date: Moment,
    dates: ReadonlyArray<string>,
    selectedDate: string,
    timezone: string,
    timespan: Timespan,
    openDate: Moment | null,
): ReadonlyArray<DayData | null> {
    const today = moment().tz(timezone);
    const todayString = today.format(DATE_TO_STRING_FORMAT);

    // Transform `dates` to contain only unique days (ignoring time)
    const uniqueDays = Array.from(
        new Set(
            dates.map((dateTime) =>
                moment
                    .tz(dateTime, moment.ISO_8601, timezone)
                    .format(DATE_TO_STRING_FORMAT),
            ),
        ),
    ).sort();

    const currentDay = date.clone().startOf(timespan);
    const nextDay = () => currentDay.add(1, 'day');
    let days: Array<DayData | null> = [];

    // Fill initial padding for month view if needed
    if (timespan === Timespan.Month) {
        days = days.concat(new Array(currentDay.day()).fill(null));
    }

    // Loop through each day in the selected timespan (week or month)
    while (currentDay.isSame(date, timespan)) {
        const hasSlots = uniqueDays.includes(
            currentDay.format(DATE_TO_STRING_FORMAT),
        );

        days.push(
            createDay(
                currentDay.clone(), // Clone to avoid mutating currentDay
                hasSlots,
                selectedDate,
                todayString,
                openDate,
            ),
        );

        nextDay();
    }

    // Fill trailing nulls for the month view to complete the calendar grid
    if (timespan === Timespan.Month) {
        days = days.concat(new Array(6 - currentDay.day()).fill(null));
    }

    return days;
}

function createTimeControl(
    date: Moment,
    timezone: string,
    timespan: Timespan,
    setTime: (newDate: Moment) => void,
    openDate?: Moment | null,
): TimeControl {
    const hasNext = moment()
        .tz(timezone)
        .add(maxFuture(timespan), `${timespan}s`)
        .isAfter(date, timespan);
    const hasNextYear = moment()
        .tz(timezone)
        .add(maxFuture(Timespan.Year), 'years')
        .isAfter(date, 'year');
    const startDate = openDate ? moment.max(openDate, moment()) : moment();
    const hasPrevious = date.isAfter(startDate.toDate(), timespan);
    const hasPreviousYear = date.isAfter(startDate.toDate(), 'year');

    const getMinDate = () => {
        return moment
            .tz(timezone)
            .startOf(timespan === Timespan.Week ? 'week' : 'month');
    };

    const next = () => {
        if (hasNext) {
            setTime(date.clone().add(1, timespan));
        }
    };

    const nextYear = () => {
        if (hasNext) {
            setTime(date.clone().add(1, 'year'));
        }
    };

    const previous = () => {
        if (hasPrevious) {
            const newDate = date.clone().subtract(1, timespan);
            const minDate = getMinDate();

            if (minDate.isAfter(newDate)) {
                setTime(minDate);
            } else {
                setTime(newDate);
            }
        }
    };

    const previousYear = () => {
        if (hasPrevious) {
            const newDate = date.clone().subtract(1, 'year');
            const minDate = getMinDate();

            if (minDate.isAfter(newDate)) {
                setTime(minDate);
            } else {
                setTime(newDate);
            }
        }
    };

    return {
        hasNext,
        hasNextYear,
        hasPrevious,
        hasPreviousYear,
        next,
        nextYear,
        previous,
        previousYear,
    };
}

function getInitialTimespan(
    selectedDate: string,
    timezone: string,
    timespan: Timespan,
): moment.Moment {
    let initialDate = moment().tz(timezone);
    if (selectedDate && selectedDate !== '') {
        initialDate = moment.tz(selectedDate, DATE_TO_STRING_FORMAT, timezone);
    }

    return initialDate.startOf(timespan);
}

export default function useTimeSlots({
    initialDate,
    initialTime,
    locationId,
    locationOpenDate,
    useWeeklySchedule,
}: TimeSlotsProps): TimeSlotsControls {
    const previousLocationId = usePrevious(locationId);
    const timezone = useLocationTimeZone(locationId);
    const openDate = React.useMemo(
        () => (locationOpenDate ? moment(locationOpenDate) : null),
        [locationOpenDate],
    );
    const initialDateMoment = initialDate ? moment(initialDate) : null;
    const preselectedDay = React.useMemo(() => {
        const currentDate = moment();
        let day = initialDateMoment?.isAfter(currentDate, 'day')
            ? initialDateMoment
            : currentDate;

        if (openDate && day.isBefore(openDate, 'day')) {
            day = openDate;
        }

        return day;
    }, [initialDateMoment, openDate]);

    const [selectedDate, selectDate] = React.useState(
        preselectedDay.format('YYYY-MM-DD'),
    );

    const [week, setWeek] = React.useState(
        getInitialTimespan(selectedDate, timezone, Timespan.Week),
    );
    const [month, setMonth] = React.useState(
        getInitialTimespan(selectedDate, timezone, Timespan.Month),
    );
    const [year, setYear] = React.useState(
        getInitialTimespan(selectedDate, timezone, Timespan.Year),
    );
    const [previousWeekValue, setPreviousWeekValue] = React.useState(
        week.clone(),
    );
    const [previousMonthValue, setPreviousMonthValue] = React.useState(
        month.clone(),
    );
    const [previousYearValue, setPreviousYearValue] = React.useState(
        year.clone(),
    );

    const debouncedWeek = useDebounced(week, 600);
    const debouncedMonth = useDebounced(month, 600);
    const debouncedYear = useDebounced(year, 600);

    // accommodate for timezone issues at the end of the month
    React.useEffect(() => {
        const currentLocationMonth = moment().tz(timezone);

        if (currentLocationMonth.isAfter(month, 'month')) {
            setMonth(
                getInitialTimespan(selectedDate, timezone, Timespan.Month),
            );
        }
    }, [timezone, month, selectedDate]);

    // reset to initial month on location change
    React.useEffect(() => {
        if (locationId && locationId !== previousLocationId) {
            setMonth(
                getInitialTimespan(selectedDate, timezone, Timespan.Month),
            );
            setWeek(getInitialTimespan(selectedDate, timezone, Timespan.Week));
            setYear(getInitialTimespan(selectedDate, timezone, Timespan.Year));
        }
    }, [locationId, previousLocationId, selectedDate, timezone]);

    const [provider, setProvider] = React.useState<Provider>(
        useSelector(getProvider),
    );
    const [selectedSlot, selectSlot] = React.useState(initialTime);
    const [booted, setBooted] = React.useState(false);

    React.useEffect(() => {
        setBooted(true);
    }, []);

    // `selectedDate` is just the date (like f.e. 2023-03-31) - it doesn't
    // hold any time information. If we construct it in
    // the user timezone and then apply a different timezone
    // that is behind the user's timezone, it will subtract a
    // number of hours that is > 0 from the date, which was
    // already 0am. Hence, it will be displayed as the day
    // before the actual date.
    const selectedDateMMMMDYYYY = React.useMemo(
        () => moment(selectedDate).format('MMMM D, YYYY'),
        [selectedDate],
    );
    const selectedDateString = React.useMemo(
        () => moment(selectedDate).format('MMM D'),
        [selectedDate],
    );
    const selectedDateStringWithDay = React.useMemo(
        () => moment(selectedDate).format('dddd, MMM D'),
        [selectedDate],
    );

    // same goes for today: we only want to display the current day
    // to the user, not the time.
    const todayYYYYMMDD = moment().format(DATE_TO_STRING_FORMAT);

    React.useEffect(() => {
        selectSlot(initialTime);
    }, [initialTime]);

    const [weekDates, setWeekDates] = React.useState<ReadonlyArray<string>>([]);
    const [monthDates, setMonthDates] = React.useState<ReadonlyArray<string>>(
        [],
    );
    const [monthDays, setMonthDays] = React.useState<
        ReadonlyArray<DayData | null>
    >([]);
    const [weekDays, setWeekDays] = React.useState<
        ReadonlyArray<DayData | null>
    >([]);

    const [ungroupedSlots, setUngroupedSlots] =
        React.useState<ReadonlyArray<Slot> | null>(null);
    const [slots, setSlots] =
        React.useState<ReadonlyArray<GroupedSlots> | null>(null);
    const [allSlots, setAllSlots] = React.useState<ReadonlyArray<Slot> | null>(
        null,
    );
    const [weekProviders, setWeekProviders] = React.useState<
        ReadonlyArray<ProvidersResult>
    >([]);
    const [monthProviders, setMonthProviders] = React.useState<
        ReadonlyArray<ProvidersResult>
    >([]);

    const [
        {
            hasPrevious: hasPreviousMonth,
            hasPreviousYear: hasPreviousMonthYear,
            hasNext: hasNextMonth,
            hasNextYear: hasNextMonthYear,
            previous: previousMonth,
            previousYear: previousMonthYear,
            next: nextMonth,
            nextYear: nextMonthYear,
        },
        setMonthControl,
    ] = React.useState(() =>
        createTimeControl(month, timezone, Timespan.Month, setMonth, openDate),
    );

    const [
        {
            hasPrevious: hasPreviousWeek,
            hasPreviousYear: hasPreviousWeekYear,
            hasNext: hasNextWeek,
            hasNextYear: hasNextWeekYear,
            previous: previousWeek,
            previousYear: previousWeekYear,
            next: nextWeek,
            nextYear: nextWeekYear,
        },
        setWeekControl,
    ] = React.useState(() =>
        createTimeControl(week, timezone, Timespan.Week, setWeek, openDate),
    );

    const weekDaysVars = React.useMemo(() => {
        return createDatesVariables(
            locationId ?? '',
            debouncedWeek,
            timezone,
            Timespan.Week,
        );
    }, [debouncedWeek, locationId, timezone]);

    const monthDaysVars = React.useMemo(() => {
        return createDatesVariables(
            locationId ?? '',
            debouncedMonth,
            timezone,
            Timespan.Month,
        );
    }, [debouncedMonth, locationId, timezone]);

    const { loading: isLoadingMonthDates, data: monthDatesData } =
        useBookingQuery<TimeSlotsQuery, TimeSlotsVariables>(query, {
            fetchPolicy: 'no-cache',
            context: {
                clientName: GRAPHQL_CLIENT_NAMES.booking,
            },
            skip: !booted || useWeeklySchedule,
            variables: monthDaysVars,
        });

    const { loading: isLoadingWeekDates, data: weekDatesData } =
        useBookingQuery<TimeSlotsQuery, TimeSlotsVariables>(query, {
            fetchPolicy: 'no-cache',
            context: {
                clientName: GRAPHQL_CLIENT_NAMES.booking,
            },
            skip: !booted || !useWeeklySchedule,
            variables: weekDaysVars,
        });

    React.useEffect(() => {
        setMonthControl(
            createTimeControl(
                month,
                timezone,
                Timespan.Month,
                setMonth,
                openDate,
            ),
        );
        setWeekControl(
            createTimeControl(week, timezone, Timespan.Week, setWeek, openDate),
        );
    }, [month, timezone, week, year, setYear, setMonth, setWeek, openDate]);

    const [loadSlots, { loading: isLoadingSlots, data: slotsData }] =
        useLazyQuery<TimeSlotsQuery, TimeSlotsVariables>(query, {
            fetchPolicy: 'no-cache',
            context: {
                clientName: GRAPHQL_CLIENT_NAMES.booking,
            },
        });

    const nextWeekDate = React.useMemo(
        () =>
            moment().tz(timezone).add(1, 'week').format(DATE_TO_STRING_FORMAT),
        [timezone],
    );

    const isNextWeek = React.useMemo(
        () => weekDays.filter((day) => day?.date === nextWeekDate).length > 0,
        [weekDays, nextWeekDate],
    );

    React.useEffect(() => {
        let handle: number | undefined;
        if (locationId && booted) {
            handle = requestAnimationFrame(() => {
                handle = requestAnimationFrame(() => {
                    handle = undefined;

                    const locationHasChanged =
                        previousLocationId && previousLocationId !== locationId;
                    const weekChanged =
                        previousWeekValue &&
                        !previousWeekValue.isSame(debouncedWeek);
                    const monthChanged =
                        previousMonthValue &&
                        !previousMonthValue.isSame(debouncedMonth);
                    const yearChanged =
                        previousYearValue &&
                        !previousYearValue.isSame(debouncedYear);

                    if (locationHasChanged) {
                        setWeekDates([]);
                        setMonthDates([]);
                        setSlots([]);
                    }

                    if (weekChanged || monthChanged || yearChanged) {
                        // user switched location, week, or month
                        setPreviousWeekValue(debouncedWeek.clone());
                        setPreviousMonthValue(debouncedMonth.clone());
                        setPreviousYearValue(debouncedYear.clone());
                    }
                });
            });
        }

        return () => {
            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    }, [
        locationId,
        previousLocationId,
        previousWeekValue,
        previousMonthValue,
        debouncedWeek,
        debouncedMonth,
        timezone,
        debouncedYear,
        previousYearValue,
        booted,
    ]);

    React.useEffect(() => {
        let handle: number | undefined;

        if (weekDatesData?.timeSlotsWithProviders) {
            const { slotsWithProviders } = weekDatesData.timeSlotsWithProviders;
            const dateStrings = filterProviderSlots(
                slotsWithProviders,
                provider,
            );
            const allProviders = slotsWithProviders
                .map(({ provider: thisProvider }) => thisProvider)
                .filter((p) => !p.isRelief);
            const providersUniq = uniqBy(allProviders, 'id');
            handle = requestAnimationFrame(() => {
                handle = requestAnimationFrame(() => {
                    handle = undefined;

                    setWeekDates(dateStrings);
                    setWeekProviders(providersUniq);
                });
            });
        }

        return () => {
            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    }, [provider, weekDatesData]);

    React.useEffect(() => {
        let handle: number | undefined;

        if (monthDatesData?.timeSlotsWithProviders) {
            const { slotsWithProviders } =
                monthDatesData.timeSlotsWithProviders;
            const dateStrings = filterProviderSlots(
                slotsWithProviders,
                provider,
            );
            const allProviders = slotsWithProviders
                .map(({ provider: thisProvider }) => thisProvider)
                .filter((p) => !p.isRelief);
            const providersUniq = uniqBy(allProviders, 'id');
            handle = requestAnimationFrame(() => {
                handle = requestAnimationFrame(() => {
                    handle = undefined;

                    setMonthDates(dateStrings);
                    setMonthProviders(providersUniq);
                });
            });
        }

        return () => {
            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    }, [monthDatesData, provider]);

    React.useEffect(() => {
        let handle: number | undefined;
        handle = requestAnimationFrame(() => {
            handle = requestAnimationFrame(() => {
                handle = undefined;
                setSlots([]);

                if (selectedDate && locationId) {
                    loadSlots({
                        variables: createSlotVariables(
                            locationId,
                            selectedDate,
                            timezone,
                        ),
                    });
                }
            });
        });

        return () => {
            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    }, [selectedDate, loadSlots, locationId, timezone]);

    React.useEffect(() => {
        if (isLoadingSlots) {
            setSlots(null);
            setUngroupedSlots(null);
            setAllSlots(null);
        }
    }, [isLoadingSlots]);

    React.useEffect(() => {
        let handle: number | undefined;
        if (slotsData?.timeSlotsWithProviders) {
            const { slotsWithProviders } = slotsData.timeSlotsWithProviders;
            const slotTimes = filterProviderSlots(slotsWithProviders, provider);
            const allSlotTimes = filterProviderSlots(slotsWithProviders, null);
            handle = requestAnimationFrame(() => {
                handle = requestAnimationFrame(() => {
                    setUngroupedSlots(
                        slotTimes.map((time) => ({
                            label: moment(time, moment.ISO_8601)
                                .tz(timezone)
                                .format('LT'),
                            time,
                        })),
                    );
                    setAllSlots(
                        allSlotTimes.map((time) => ({
                            label: moment(time, moment.ISO_8601)
                                .tz(timezone)
                                .format('LT'),
                            time,
                        })),
                    );
                    setSlots(groupSlots(slotTimes, timezone));
                });
            });
        } else {
            setSlots(null);
            setUngroupedSlots(null);
        }

        return () => {
            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    }, [slotsData, timezone, provider]);

    React.useEffect(() => {
        let handle: number | undefined = requestAnimationFrame(() => {
            handle = requestAnimationFrame(() => {
                handle = undefined;
                setMonthDays(
                    createTimespanDays(
                        month,
                        monthDates,
                        selectedDate,
                        timezone,
                        Timespan.Month,
                        openDate,
                    ),
                );
            });
        });

        return () => {
            if (handle) {
                cancelAnimationFrame(handle);
            }
        };
    }, [monthDates, month, timezone, selectedDate, openDate]);

    React.useEffect(() => {
        let handle: number | undefined = requestAnimationFrame(() => {
            handle = requestAnimationFrame(() => {
                handle = undefined;
                setWeekDays(
                    createTimespanDays(
                        week,
                        weekDates,
                        selectedDate,
                        timezone,
                        Timespan.Week,
                        openDate,
                    ),
                );
            });
        });

        return () => {
            if (handle) {
                cancelAnimationFrame(handle);
            }
        };
    }, [weekDates, week, timezone, selectedDate, openDate]);

    return {
        locationId,
        year,
        month,
        allSlots,
        week,
        monthControl: {
            next: nextMonth,
            nextYear: nextMonthYear,
            previous: previousMonth,
            previousYear: previousMonthYear,
            hasNext: hasNextMonth,
            hasNextYear: hasNextMonthYear,
            hasPrevious: hasPreviousMonth,
            hasPreviousYear: hasPreviousMonthYear,
        },
        weekControl: {
            next: nextWeek,
            nextYear: nextWeekYear,
            previous: previousWeek,
            previousYear: previousWeekYear,
            hasNext: hasNextWeek,
            hasNextYear: hasNextWeekYear,
            hasPrevious: hasPreviousWeek,
            hasPreviousYear: hasPreviousWeekYear,
        },
        weekDays,
        monthDays,
        slots,
        ungroupedSlots,
        timezone,
        isNextWeek,
        isLoadingDates:
            isLoadingMonthDates ||
            isLoadingWeekDates ||
            debouncedMonth.valueOf() !== month.valueOf(),
        isLoadingSlots,
        selectDate,
        selectSlot,
        weekProviders,
        monthProviders,
        provider,
        setProvider,
        selectedDate,
        selectedSlot,
        selectedDateString,
        selectedDateStringWithDay,
        todayYYYYMMDD,
        selectedDateMMMMDYYYY,
        daysOfTheWeek: DAYS_OF_THE_WEEK,
    };
}
